import { createMachine, assign, fromCallback } from 'xstate';
import { trustyConstants as c } from './trusty.constants';
import { trustyService } from './trustyService';
import { playSound } from './trustyAudio';
import { gameConfig } from './trusty.config';
import { appConstants } from '../../../shared/constants/appConstants';

export const trustyMachine = createMachine({
  id: 'trustyGame',
  initial: c.states.idle,
  context: {
    items: [],
    currentPair: null,
    score: 0,
    set1Score: 0,
    set2Score: 0,
    highScore: 0,
    streak: 0,
    timer: 0,
    scoreFeedback: null,
    timerFeedback: null,
    config: gameConfig.scoring,
    set1Langs: gameConfig.languages.set1,
    set2Langs: gameConfig.languages.set2
  },
  states: {
    [c.states.idle]: {
      on: {
        [c.events.START]: {
          target: c.states.set1Ready,
          actions: [
            assign(({ event }) => {
              const items = trustyService.fetchGameData(event.lessonId);
              const HS_KEY = appConstants.localStorageKeyTrustyHighScore;
              const savedHS = localStorage.getItem(HS_KEY);
              return {
                items,
                score: 0,
                set1Score: 0,
                set2Score: 0,
                streak: 0,
                highScore: savedHS ? parseInt(savedHS, 10) : 0,
                scoreFeedback: null,
                timerFeedback: null,
                set1Langs: gameConfig.languages.set1,
                set2Langs: gameConfig.languages.set2
              };
            })
          ]
        }
      }
    },

    [c.states.set1Ready]: {
      on: {
        [c.events.START]: {
          target: 'set1Playing',
          actions: [
            assign(({ context, event }) => {
              const set1Langs = event.languages?.set1 || context.set1Langs;
              const set2Langs = event.languages?.set2 || context.set2Langs;
              return {
                timer: Math.min(Math.round(context.items.length * gameConfig.timing.timePerWord), gameConfig.timing.timeCapMax),
                score: 0,
                streak: 0,
                set1Langs,
                set2Langs,
                currentPair: trustyService.generatePair(context.items, set1Langs.q, set1Langs.a),
                scoreFeedback: null,
                timerFeedback: null
              };
            })
          ]
        }
      }
    },

    set1Playing: {
      type: 'parallel',
      states: {
        timer: {
          invoke: {
            src: fromCallback(({ sendBack }) => {
              const interval = setInterval(() => sendBack({ type: c.events.TICK }), gameConfig.timing.tickInterval);
              return () => clearInterval(interval);
            })
          },
          on: {
            [c.events.TICK]: {
              actions: assign({ timer: ({ context }) => Math.max(0, context.timer - 1) })
            }
          }
        },
        gameplay: {
          on: {
            [c.events.PAUSE]: { target: '#trustyGame.paused1' },
            [c.events.GUESS]: {
              actions: [
                ({ context, event }) => {
                   const isCorrect = event.guess === context.currentPair.isCorrect;
                   playSound(isCorrect);
                },
                assign(({ context, event }) => {
                    const result = trustyService.calculateUpdate(context, event.guess, gameConfig.scoring);
                    return {
                        ...result,
                        scoreFeedback: result.feedback.score,
                        timerFeedback: result.feedback.timer,
                        currentPair: trustyService.generatePair(context.items, context.set1Langs.q, context.set1Langs.a)
                    };
                })
              ]
            }
          }
        },
        feedbackManager: {
           initial: 'idle',
           states: {
             idle: {
               on: { [c.events.GUESS]: 'active' }
             },
             active: {
               after: {
                 [gameConfig.timing.feedbackDelay]: {
                   target: 'idle',
                   actions: assign({ scoreFeedback: null, timerFeedback: null })
                 }
               },
               on: {
                 [c.events.GUESS]: {
                   target: 'active',
                   reenter: true
                 }
               }
             }
           }
        }
      },
      always: [
        { 
          target: c.states.intermediateStats, 
          guard: ({ context }) => context.timer <= 0,
          actions: assign({ set1Score: ({ context }) => context.score })
        }
      ]
    },

    [c.states.intermediateStats]: {
      on: {
        [c.events.NEXT_SET]: {
          target: c.states.set2Ready
        }
      }
    },

    [c.states.set2Ready]: {
      on: {
        [c.events.START]: {
          target: 'set2Playing',
          actions: [
            assign(({ context }) => ({
              timer: Math.min(Math.round(context.items.length * gameConfig.timing.timePerWord), gameConfig.timing.timeCapMax),
              score: 0,
              streak: 0,
              currentPair: trustyService.generatePair(context.items, context.set2Langs.q, context.set2Langs.a),
              scoreFeedback: null,
              timerFeedback: null
            }))
          ]
        }
      }
    },

    set2Playing: {
      type: 'parallel',
      states: {
        timer: {
          invoke: {
            src: fromCallback(({ sendBack }) => {
              const interval = setInterval(() => sendBack({ type: c.events.TICK }), gameConfig.timing.tickInterval);
              return () => clearInterval(interval);
            })
          },
          on: {
            [c.events.TICK]: {
              actions: assign({ timer: ({ context }) => Math.max(0, context.timer - 1) })
            }
          }
        },
        gameplay: {
          on: {
            [c.events.PAUSE]: { target: '#trustyGame.paused2' },
            [c.events.GUESS]: {
              actions: [
                ({ context, event }) => {
                   const isCorrect = event.guess === context.currentPair.isCorrect;
                   playSound(isCorrect);
                },
                assign(({ context, event }) => {
                    const result = trustyService.calculateUpdate(context, event.guess, gameConfig.scoring);
                    return {
                        ...result,
                        scoreFeedback: result.feedback.score,
                        timerFeedback: result.feedback.timer,
                        currentPair: trustyService.generatePair(context.items, context.set2Langs.q, context.set2Langs.a)
                    };
                })
              ]
            }
          }
        },
        feedbackManager: {
           initial: 'idle',
           states: {
             idle: {
               on: { [c.events.GUESS]: 'active' }
             },
             active: {
               after: {
                 [gameConfig.timing.feedbackDelay]: {
                   target: 'idle',
                   actions: assign({ scoreFeedback: null, timerFeedback: null })
                 }
               },
               on: {
                 [c.events.GUESS]: {
                   target: 'active',
                   reenter: true
                 }
               }
             }
           }
        }
      },
      always: [
        { 
          target: c.states.gameOver, 
          guard: ({ context }) => context.timer <= 0,
          actions: assign({ 
             set2Score: ({ context }) => context.score,
             score: ({ context }) => Math.round((context.set1Score + context.score) / 2)
          })
        }
      ]
    },

    [c.states.paused1]: {
        on: {
            [c.events.RESUME]: 'set1Playing'
        }
    },

    [c.states.paused2]: {
        on: {
            [c.events.RESUME]: 'set2Playing'
        }
    },

    [c.states.gameOver]: {
      entry: ({ context }) => {
        if (context.score > context.highScore) {
          localStorage.setItem(appConstants.localStorageKeyTrustyHighScore, String(context.score));
        }
      },
      on: {
        [c.events.RETRY]: {
          target: c.states.set1Ready,
          actions: [
            assign({
              score: 0,
              set1Score: 0,
              set2Score: 0,
              streak: 0,
              scoreFeedback: null,
              timerFeedback: null
            })
          ]
        },
        [c.events.EXIT]: c.states.idle
      }
    }
  }
});
